import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { SkelStats, SkelCardGrid } from '../components/Skeletons';
import '../ui-pages.css';

// WhatsApp brand glyph (matches Integrations / Chats)
const WhatsAppGlyph = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.414 0 .004 5.408.001 12.045a11.815 11.815 0 001.591 5.976L0 24l6.117-1.605a11.782 11.782 0 005.925 1.586h.005c6.637 0 12.048-5.409 12.051-12.047a11.816 11.816 0 00-3.576-8.519"/></svg>
);

const STATUS_TONE = {
  APPROVED: { color: '#0F6E56', bg: '#E4F1E9' },
  PENDING:  { color: '#8A5A12', bg: '#F5EAD5' },
  REJECTED: { color: '#B23A3A', bg: '#F6E4E1' },
};
const statusTone = (s) => STATUS_TONE[s] || { color: '#9C978C', bg: '#FAF7F1' };

// Sample templates shown on demo (read-only) accounts, where the live WhatsApp
// Manager connection is intentionally neutralized. Shape matches Meta's API.
const DEMO_TEMPLATES = [
  { id: 'demo-1', name: 'order_confirmation', status: 'APPROVED', category: 'UTILITY', language: 'en_US',
    components: [{ type: 'BODY', text: 'Hi {{1}}! 🎉 Your order {{2}} has been confirmed. We\'ll notify you once it ships. Thank you for shopping with us!' }, { type: 'BUTTONS', buttons: [{ text: 'Track Order' }, { text: 'View Details' }] }] },
  { id: 'demo-2', name: 'order_shipped', status: 'APPROVED', category: 'UTILITY', language: 'en_US',
    components: [{ type: 'BODY', text: 'Good news {{1}}! 📦 Your order {{2}} is on its way. Tracking number: {{3}}. Estimated delivery in 2-4 days.' }, { type: 'BUTTONS', buttons: [{ text: 'Track Shipment' }] }] },
  { id: 'demo-3', name: 'abandoned_cart', status: 'APPROVED', category: 'MARKETING', language: 'en_US',
    components: [{ type: 'BODY', text: 'Hey {{1}}, you left something behind! 🛍️ Your cart is waiting. Complete your order now and enjoy free delivery on us.' }, { type: 'BUTTONS', buttons: [{ text: 'Complete Order' }, { text: 'View Cart' }] }] },
  { id: 'demo-4', name: 'delivery_feedback', status: 'APPROVED', category: 'UTILITY', language: 'en_US',
    components: [{ type: 'BODY', text: 'Hi {{1}}, your order {{2}} has been delivered! 💜 How was your experience? Your feedback helps us serve you better.' }, { type: 'BUTTONS', buttons: [{ text: 'Rate Us' }, { text: 'Contact Support' }] }] },
  { id: 'demo-5', name: 'new_collection_launch', status: 'APPROVED', category: 'MARKETING', language: 'en_US',
    components: [{ type: 'BODY', text: '✨ New arrivals just dropped, {{1}}! Explore our latest collection with exclusive early-access pricing for our loyal customers.' }, { type: 'BUTTONS', buttons: [{ text: 'Shop Now' }] }] },
  { id: 'demo-6', name: 'payment_reminder', status: 'PENDING', category: 'UTILITY', language: 'en_US',
    components: [{ type: 'BODY', text: 'Hi {{1}}, a gentle reminder that payment for order {{2}} is pending. Please complete it to avoid cancellation.' }, { type: 'BUTTONS', buttons: [{ text: 'Pay Now' }] }] },
  { id: 'demo-7', name: 'discount_offer', status: 'APPROVED', category: 'MARKETING', language: 'en_US',
    components: [{ type: 'BODY', text: '🎁 Special offer just for you, {{1}}! Get 20% off your next purchase with code SAVE20. Valid for 48 hours only.' }, { type: 'BUTTONS', buttons: [{ text: 'Redeem Offer' }] }] },
];

const WhatsAppTemplates = () => {
    const { workspaceUuid } = useParams();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [filter, setFilter] = useState('ALL');
    const [search, setSearch] = useState('');
    const [newTemplate, setNewTemplate] = useState({
        name: '',
        category: 'UTILITY',
        language: 'en_US',
        body: 'Hello! Your order {{1}} has been confirmed. Thank you for shopping with us!',
        buttons: [
            { type: 'QUICK_REPLY', text: 'Confirm Order' },
            { type: 'QUICK_REPLY', text: 'View Status' }
        ]
    });

    const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

    useEffect(() => {
        fetchTemplates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspaceUuid]);

    const fetchTemplates = async () => {
        setLoading(true);
        // Demo (read-only) accounts: WhatsApp is neutralized, so show sample
        // templates instead of hitting the live (disconnected) Meta connection.
        if (localStorage.getItem('readOnly') === '1') {
            setTemplates(DEMO_TEMPLATES);
            setLoading(false);
            return;
        }
        try {
            const res = await axios.get(`${API_URL}/api/integrations/whatsapp/templates?workspaceUuid=${workspaceUuid}`);
            setTemplates(res.data.data || []);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching templates:', err);
            setLoading(false);
        }
    };

    const handleCreateTemplate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const templateData = {
                name: newTemplate.name.toLowerCase().replace(/\s+/g, '_'),
                category: newTemplate.category,
                language: newTemplate.language,
                components: [
                    { type: 'BODY', text: newTemplate.body }
                ]
            };

            if (newTemplate.buttons.length > 0) {
                templateData.components.push({ type: 'BUTTONS', buttons: newTemplate.buttons });
            }

            await axios.post(`${API_URL}/api/integrations/whatsapp/templates`, {
                workspaceUuid,
                templateData
            });
            setShowCreateModal(false);
            fetchTemplates();
        } catch (err) {
            alert('Error creating template: ' + (err.response?.data?.error?.message || err.message));
        } finally {
            setSubmitting(false);
        }
    };

    const addButton = () => {
        if (newTemplate.buttons.length >= 3) return;
        setNewTemplate({ ...newTemplate, buttons: [...newTemplate.buttons, { type: 'QUICK_REPLY', text: '' }] });
    };
    const updateButtonText = (index, text) => {
        const updated = [...newTemplate.buttons];
        updated[index].text = text;
        setNewTemplate({ ...newTemplate, buttons: updated });
    };
    const removeButton = (index) => {
        setNewTemplate({ ...newTemplate, buttons: newTemplate.buttons.filter((_, i) => i !== index) });
    };

    // ---- derived (display only) ----
    const approvedCount = templates.filter(t => t.status === 'APPROVED').length;
    const pendingCount = templates.filter(t => t.status === 'PENDING').length;
    const visible = templates.filter(t => {
        if (filter !== 'ALL' && t.status !== filter) return false;
        const q = search.trim().toLowerCase();
        if (!q) return true;
        const body = t.components?.find(c => c.type === 'BODY')?.text || '';
        return `${t.name} ${t.category} ${body}`.toLowerCase().includes(q);
    });

    const styleBlock = `
      .tpl-modal{ background:#fff; width:96%; max-width:980px; height:80vh; border-radius:20px; overflow:hidden; display:flex; position:relative; box-shadow:4px 4px 0 rgba(17,17,17,.2); }
      .tpl-form{ flex:1; padding:36px 40px; overflow-y:auto; }
      .tpl-preview{ width:380px; background:#FAF7F1; border-left:1px solid #E7E1D6; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:36px; }
      .tpl-input{ width:100%; border:1px solid #E7E1D6; border-radius:14px; padding:12px 13px; font-size:14px; outline:none; box-sizing:border-box; color:#111111; background:#fff; }
      .tpl-input:focus{ border-color:#6B6862; }
      .tpl-label{ display:block; font-size:12px; font-weight:700; color:#3A3A37; margin-bottom:7px; }
      @media (max-width:768px){
        .tpl-modal{ height:90vh; max-height:90vh; }
        .tpl-preview{ display:none; }
        .tpl-form{ padding:26px 20px; }
      }
    `;

    if (loading && !showCreateModal) {
        return (
            <div className="ui-page">
                <header className="ui-head">
                    <div>
                        <h1 className="ui-title">Message Templates</h1>
                        <p className="ui-sub">Synced live from your WhatsApp Manager. Create new templates and submit them to Meta for review.</p>
                    </div>
                </header>
                <SkelStats count={4} />
                <SkelCardGrid count={6} minWidth={300} />
            </div>
        );
    }

    return (
        <div className="ui-page">
            <style>{styleBlock}</style>

            <header className="ui-head">
                <div>
                    <h1 className="ui-title">Message Templates</h1>
                    <p className="ui-sub">Synced live from your WhatsApp Manager. Create new templates and submit them to Meta for review.</p>
                </div>
                <div className="ui-head-actions">
                    <button className="ui-btn ui-btn-ghost" onClick={fetchTemplates}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
                        Refresh
                    </button>
                    <button className="ui-btn" onClick={() => setShowCreateModal(true)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        New Template
                    </button>
                </div>
            </header>

            {/* stats */}
            <div className="ui-stats">
                <div className="ui-stat tone-emerald">
                    <span className="ui-stat-ic"><WhatsAppGlyph size={18} /></span>
                    <div><div className="ui-stat-n">{templates.length}</div><div className="ui-stat-l">Total Templates</div></div>
                </div>
                <div className="ui-stat tone-emerald">
                    <span className="ui-stat-ic"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg></span>
                    <div><div className="ui-stat-n">{approvedCount}</div><div className="ui-stat-l">Approved</div></div>
                </div>
                <div className="ui-stat tone-amber">
                    <span className="ui-stat-ic"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg></span>
                    <div><div className="ui-stat-n">{pendingCount}</div><div className="ui-stat-l">Pending Review</div></div>
                </div>
            </div>

            {/* filter tabs + search */}
            <div className="ui-tabs">
                {[
                    { k: 'ALL', label: 'All', n: templates.length },
                    { k: 'APPROVED', label: 'Approved', n: approvedCount },
                    { k: 'PENDING', label: 'Pending', n: pendingCount },
                ].map(t => (
                    <button key={t.k} className={`ui-tab${filter === t.k ? ' on' : ''}`} onClick={() => setFilter(t.k)}>
                        {t.label}<span className="ui-tab-n">{t.n}</span>
                    </button>
                ))}
                <div style={{ marginLeft: 'auto' }}>
                    <div className="ui-search">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                        <input placeholder="Search templates…" value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                </div>
            </div>

            {/* grid / empty */}
            {visible.length === 0 ? (
                <div className="ui-empty">
                    <div className="ui-empty-ic"><WhatsAppGlyph size={26} /></div>
                    <h2>{templates.length === 0 ? 'No templates yet' : 'No matches'}</h2>
                    <p>{templates.length === 0
                        ? 'Create your first template — it’ll be submitted to Meta for review and appear here once processed.'
                        : 'No templates match your current filter or search.'}</p>
                    {templates.length === 0 && (
                        <button className="ui-btn" style={{ margin: '0 auto' }} onClick={() => setShowCreateModal(true)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                            New Template
                        </button>
                    )}
                </div>
            ) : (
                <div className="ui-grid">
                    {visible.map((tmpl, i) => {
                        const tone = statusTone(tmpl.status);
                        const body = tmpl.components?.find(c => c.type === 'BODY')?.text || 'No body content';
                        const btns = tmpl.components?.find(c => c.type === 'BUTTONS')?.buttons || [];
                        return (
                            <div key={tmpl.id || tmpl.name} className="ui-card" style={{ display: 'flex', flexDirection: 'column', animationDelay: `${Math.min(i * 40, 320)}ms` }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                                    <span className="ui-pill" style={{ color: tone.color, background: tone.bg }}>
                                        <span className="ui-dot" style={{ background: tone.color }} />{tmpl.status}
                                    </span>
                                    <span className="ui-chip" style={{ textTransform: 'none' }}>{tmpl.category}</span>
                                </div>
                                <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, letterSpacing: '-0.03em', color: '#111111', wordBreak: 'break-word' }}>{tmpl.name}</h3>
                                <div style={{ flex: 1, background: '#FAF7F1', border: '1px solid #E7E1D6', borderRadius: 14, padding: 14, marginBottom: btns.length ? 14 : 0 }}>
                                    <p style={{ fontSize: 13.5, color: '#3A3A37', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap', fontFamily: "'IBM Plex Mono', monospace" }}>{body}</p>
                                </div>
                                {btns.length > 0 && (
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {btns.map((btn, bi) => (
                                            <span key={bi} className="ui-chip" style={{ textTransform: 'none', fontWeight: 700 }}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
                                                {btn.text}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* CREATE MODAL */}
            {showCreateModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,17,17,0.3)', backdropFilter: 'blur(2px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowCreateModal(false); }}>
                    <div className="tpl-modal">
                        <button
                            onClick={() => setShowCreateModal(false)}
                            style={{ position: 'absolute', top: 18, right: 18, width: 32, height: 32, borderRadius: '50%', background: '#fff', border: '1px solid #E7E1D6', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10, color: '#6B6862' }}
                            aria-label="Close"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>

                        {/* FORM */}
                        <div className="tpl-form">
                            <div style={{ marginBottom: 26 }}>
                                <h2 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.03em', color: '#111111', margin: '0 0 4px' }}>Create Template</h2>
                                <p style={{ color: '#6B6862', fontSize: 13.5, margin: 0 }}>Configure your message and submit it to Meta for review.</p>
                            </div>

                            <form onSubmit={handleCreateTemplate}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <label className="tpl-label">Template Name</label>
                                        <input type="text" className="tpl-input" value={newTemplate.name}
                                            onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                                            placeholder="e.g. shipping_update" required />
                                    </div>
                                    <div>
                                        <label className="tpl-label">Category</label>
                                        <select className="tpl-input" value={newTemplate.category} onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}>
                                            <option value="UTILITY">Utility (Transactional)</option>
                                            <option value="MARKETING">Marketing (Promotional)</option>
                                        </select>
                                    </div>
                                </div>

                                <div style={{ marginTop: 18 }}>
                                    <label className="tpl-label">Message Content</label>
                                    <textarea className="tpl-input" style={{ minHeight: 120, lineHeight: 1.6, resize: 'vertical', fontFamily: "'IBM Plex Mono', monospace" }}
                                        value={newTemplate.body} onChange={(e) => setNewTemplate({ ...newTemplate, body: e.target.value })}
                                        placeholder="Use {{1}}, {{2}} for dynamic values…" required />
                                </div>

                                <div style={{ marginTop: 24 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                        <label className="tpl-label" style={{ marginBottom: 0 }}>Interactive Buttons</label>
                                        {newTemplate.buttons.length < 3 && (
                                            <button type="button" onClick={addButton} style={{ background: 'transparent', border: 'none', color: '#111111', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>+ Add option</button>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        {newTemplate.buttons.map((btn, idx) => (
                                            <div key={idx} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                <input type="text" className="tpl-input" value={btn.text}
                                                    onChange={(e) => updateButtonText(idx, e.target.value)} placeholder="e.g. Yes, please" required />
                                                <button type="button" onClick={() => removeButton(idx)}
                                                    className="ui-btn ui-btn-danger" style={{ width: 44, height: 44, padding: 0, justifyContent: 'center', flex: 'none' }} aria-label="Remove button">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: 12, marginTop: 32 }}>
                                    <button type="button" className="ui-btn ui-btn-ghost" style={{ flex: 1, justifyContent: 'center', padding: '12px' }} onClick={() => setShowCreateModal(false)}>Discard</button>
                                    <button type="submit" className="ui-btn" style={{ flex: 2, justifyContent: 'center', padding: '12px' }} disabled={submitting}>
                                        {submitting ? 'Submitting to Meta…' : 'Submit for Meta Review'}
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* LIVE PREVIEW */}
                        <div className="tpl-preview">
                            <span style={{ fontSize: 11, fontWeight: 800, color: '#9C978C', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 24, fontFamily: "'IBM Plex Mono', monospace" }}>Live Preview</span>
                            <div style={{ width: 280, background: '#e5e7eb', borderRadius: 36, border: '8px solid #111111', boxShadow: '4px 4px 0 rgba(17,17,17,.2)', overflow: 'hidden' }}>
                                <div style={{ height: 22, background: '#111111' }} />
                                <div style={{ background: '#e5ddd5', minHeight: 420, padding: 16, display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ background: 'white', borderRadius: '0 12px 12px 12px', padding: 12, boxShadow: '0 1px 2px rgba(0,0,0,0.1)', maxWidth: '92%' }}>
                                        <p style={{ fontSize: 13, color: '#111111', lineHeight: 1.4, margin: 0, whiteSpace: 'pre-wrap', fontFamily: "'IBM Plex Mono', monospace" }}>{newTemplate.body || 'Your message starts here…'}</p>
                                        <div style={{ textAlign: 'right', fontSize: 10, color: '#6B6862', marginTop: 4 }}>12:00 PM</div>
                                    </div>
                                    {newTemplate.buttons.length > 0 && (
                                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {newTemplate.buttons.map((btn, i) => (
                                                <div key={i} style={{ background: 'white', borderRadius: 8, padding: 10, textAlign: 'center', color: '#0ea5e9', fontSize: 13, fontWeight: 600, boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>{btn.text || 'Button text…'}</div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WhatsAppTemplates;
